import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import * as yup from "yup"; //M10-12438
import InvoiceLayout from "src/pages/invoice-master/invoice-layout.component";
import { Button, Spinner } from "src/components";
import DeleteModal from "src/container/DeleteModal"; //M10-12438
import { useToast } from "src/hooks/useToast";
import { getBatchPoList, getBatchPoPayloadList, getPoApiPushConfig, getPoGeneration, removePoApi, updatePoRepushFlag } from "src/services/po-api"; //M10-13060 //M10-14101
import { RootState } from "src/context/store";
import { fineGrainedPermissionsKeys } from "src/constants/permission";  //M10-12438

// PO types that can be API-submitted — only CN-related types (CNRP, CNSH) are blocked;
// everything else (Regular, SAO, SHPO, SAO-CN) is allowed (must match assertPoSubmitAllowedType in
// backend-oms-svc, which additionally scopes SAO-CN to KAM Glenmark)
import { PO_SUBMIT_ALLOWED_TYPES } from "./po.constants";

const PoBulk = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { poBatchId } = useParams();
  const { poDetails, poFilters } = useSelector((state: RootState) => state.po);

  const [isFetchingPo, setIsFetchingPo] = useState(false);
  const [poList, setPoList] = useState<any[]>([]);
  const [loadingPoIds, setLoadingPoIds] = useState<Set<string | number>>(new Set());
  const [disabledPoIds, setDisabledPoIds] = useState<Set<string | number>>(new Set());
  const [isBulk, setIsBulk] = useState(false);
  // Whether the PO API push is configured for this batch's (company, module) — answered by OMS from
  // po_grouping_config. Defaults to false so the buttons stay disabled until the check succeeds.
  const [isApiPushEnabled, setIsApiPushEnabled] = useState(false);
  // Company-level PO re-push switch (OMS company_hdr.po_repush_applicable). When on, a PO whose push
  // succeeded but has no SO number yet can be ticked for re-push. Fails closed like the push flag.
  const [isRepushApplicable, setIsRepushApplicable] = useState(false);
  const [repushUpdatingPoIds, setRepushUpdatingPoIds] = useState<Set<string | number>>(new Set());

  const [jsonModal, setJsonModal] = useState<{ show: boolean; data?: any; filename?: string }>({
    show: false,
  });
  //M10-12438
  const [revokeModal, setRevokeModal] = useState<{ show: boolean; item?: any; loading: boolean }>({
    show: false,
    loading: false,
  });

  const revokeSchema = yup.object().shape({
    reference_number: yup
      .string()
      .required("Reference number is required")
      .max(150, "Reference number must not exceed 150 characters"),
  });
  //M10-12438
  const { idTokenDetails } = useSelector((state: RootState) => state.auth);

  /** ----------------------------------------------
   * 🔹 Fetch PO List (Memoized + Error handled)
   * --------------------------------------------- */
  const fetchPoList = useCallback(async () => {
    if (!poBatchId) return;
    setIsFetchingPo(true);

    try {
      const response = await getBatchPoList({
        po_batch_id: poBatchId,
        company_code: poFilters?.company?.code,
      });
      const fetchedPoList = response?.data?.data ?? [];
      setPoList(fetchedPoList);

      // The push implementation is per (company, module), so the batch's module decides whether the
      // submit buttons are live. Every PO in a batch shares its module, so the first row is enough.
      const moduleCode = fetchedPoList?.[0]?.module_code;
      if (fetchedPoList.length) {
        try {
          const configResponse = await getPoApiPushConfig({
            company_code: poFilters?.company?.code,
            module_code: moduleCode,
          });
          setIsApiPushEnabled(Boolean(configResponse?.data?.data?.po_api_push_enabled));
          setIsRepushApplicable(Boolean(configResponse?.data?.data?.po_repush_applicable));
        } catch (error) {
          // Fail closed — leave the submit buttons disabled rather than letting a request through
          // that the backend will reject anyway.
          console.error("Failed to fetch PO API push configuration:", error);
          setIsApiPushEnabled(false);
          setIsRepushApplicable(false);
        }
      }
    } catch (error) {
      console.error("Failed to fetch PO list:", error);
      toast({ description: "Failed to fetch PO list", variant: "destructive" });
    } finally {
      setIsFetchingPo(false);
    }
  }, [poBatchId, poFilters?.company?.code, toast]);

  useEffect(() => {
    fetchPoList();
  }, [fetchPoList]);

  /** ----------------------------------------------
   * 🔹 Utility: Loading/Disable State Management
   * --------------------------------------------- */
  const toggleLoading = useCallback((poId: string | number, isLoading: boolean) => {
    setLoadingPoIds((prev) => {
      const updated = new Set(prev);
      isLoading ? updated.add(poId) : updated.delete(poId);
      return updated;
    });

    if (!isLoading) {
      setDisabledPoIds((prev) => {
        const updated = new Set(prev);
        updated.add(poId);
        return updated;
      });
    }
  }, []);

  /** ----------------------------------------------
   * 🔹 PO Re-push
   * --------------------------------------------- */
  // Pushed successfully but no SO number yet — the only state in which a PO can be ticked for re-push.
  const isAwaitingSoNumber = (item: any) => item?.api_sent_status === "success" && !item?.so_number;

  // A ticked PO may be submitted again; the backend re-checks every condition.
  const canRepush = useCallback(
    (item: any) => isRepushApplicable && item?.api_sent_status === "success" && !item?.so_number && Boolean(item?.is_po_repush_enabled),
    [isRepushApplicable]
  );

  const handleRepushToggle = async (item: any, isEnabled: boolean) => {
    setRepushUpdatingPoIds((prev) => new Set(prev).add(item?.po_id));
    try {
      await updatePoRepushFlag({
        data: {
          po_id: item?.po_id,
          po_batch_id: item?.po_batch_id ?? Number(poBatchId),
          company_code: String(poFilters?.company?.code),
          is_po_repush_enabled: isEnabled,
        },
        event_type: "PO_REPUSH_EDIT",
        primary_key: item?.po_id,
      });
      toast({
        title: "Success",
        description: isEnabled ? `PO ${item?.po_number} enabled for re-push` : `PO ${item?.po_number} re-push disabled`,
      });
      // A PO pushed earlier in this session is hidden from Generate API; ticking it makes it pushable again.
      setDisabledPoIds((prev) => {
        const updated = new Set(prev);
        updated.delete(item?.po_id);
        return updated;
      });
      fetchPoList();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to update PO re-push";
      toast({ title: "Error", description: errorMessage, variant: "destructive" });
    } finally {
      setRepushUpdatingPoIds((prev) => {
        const updated = new Set(prev);
        updated.delete(item?.po_id);
        return updated;
      });
    }
  };

  /** ----------------------------------------------
   * 🔹 Bulk or Single Upload
   * --------------------------------------------- */
  const bulkUpload = useCallback(
    async (items: any[] | any) => {
      // Last line of defence — the buttons are already disabled when the push is not configured.
      // The authority is OMS po_grouping_config.po_api_push_enabled, not a list held here.
      if (!isApiPushEnabled) {
        return toast({
          description: "PO API push is not enabled for this company on this module.",
          variant: "destructive",
        });
      }

      const itemList = Array.isArray(items) ? items : [items];

      if (itemList.some((item) => !PO_SUBMIT_ALLOWED_TYPES.includes(item?.po_generatetype))) {
        return toast({
          description: "PO submit is not allowed for CN-related purchase orders (CNRP, CNSH).",
          variant: "destructive",
        });
      }

      setIsBulk(Array.isArray(items));

      for (const item of itemList) {
        if (item?.api_sent_status === "success" && !canRepush(item)) continue;

        toggleLoading(item?.po_id, true);

        const body = {
          data: {
            po_id: item?.po_id,
            po_number: item?.po_number,
            po_batch_id: item?.po_batch_id,
            company_code: String(poFilters?.company?.code),
            company_name: poFilters?.company?.name,
          },
          event_type: "PO_SUBMIT_ADD",
          primary_key: item?.po_id,
        };

        try {
          await getPoGeneration(body);
        } catch (error) {
          console.error("API generation failed:", error);
        } finally {
          toggleLoading(item?.po_id, false);
        }
      }

      setIsBulk(false);
      fetchPoList();
    },
    [poFilters?.company, toggleLoading, fetchPoList, toast, isApiPushEnabled, canRepush]
  );

  /** ----------------------------------------------
   * 🔹 File Download Utility
   * --------------------------------------------- */
  const downloadJson = (filename: string, jsonData: object) => {
    const blob = new Blob([JSON.stringify(jsonData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${filename}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  /** ----------------------------------------------
   * 🔹 Derived Values (Memoized)
   * --------------------------------------------- */
  const formattedPoDate = useMemo(
    () => moment(poDetails?.[0]?.created_date).format("DD-MM-YYYY"),
    [poDetails]
  );

  // CN-related PO batches (CNRP, CNSH) cannot be API-submitted; Regular/SAO/SHPO can.
  // A batch is a single PO type, so gate on every row (fail closed if po_generatetype is missing).
  const isSubmittablePoBatch = useMemo(
    () => poList.length > 0 && poList.every((po) => PO_SUBMIT_ALLOWED_TYPES.includes(po?.po_generatetype)),
    [poList]
  );

  //M10-12438
  const openRevokeModal = (item: any) => {
    setRevokeModal({ show: true, item, loading: false });
  };

  const handleRevokePo = async (formData: any) => {
    const item = revokeModal?.item;
    const referenceNumber = formData?.reference_number?.trim();

    if (!referenceNumber) {
      toast({ description: "Reference number is required", variant: "destructive" });
      return;
    }

    setRevokeModal((prev) => ({ ...prev, loading: true }));
    try {
      const body = {
        data: {
          po_id: item?.po_id,
          po_number: item?.po_number,
          type: 'removePoById',
          company_code: item?.company_code,
          reference_number: referenceNumber,
        },
        primary_key: item?.po_id,
        event_type: "PO_REMOVE",
      };

      await removePoApi(body, poFilters?.company?.code);
      toast({ title: "Success", description: "PO revoked successfully" });
      setRevokeModal({ show: false, loading: false });
      fetchPoList();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || error?.message || "Something went wrong";
      toast({ title: "Error", description: errorMessage, variant: "destructive" });
      setRevokeModal((prev) => ({ ...prev, loading: false }));
    }
  };
  //M10-14101

  //To Get Payload Data - M10-13060
  const handleDownload = async (item: any, poType: any) => {
    const res = await getBatchPoPayloadList({
      po_id: item.po_id,
      po_batch_id: item.po_batch_id,
      po_type: poType,
    });

    // Extract the required payload based on poType
    const payload = poType === "response_payload"
      ? res?.data?.data?.po_api_logs?.[0]?.response_payload
      : res?.data?.data?.po_api_logs?.[0]?.request_payload;

    // If payload is empty, show toast message
    if (!payload || payload.trim() === "") {
      toast({ description: "No data available", variant: "destructive" });
      return;
    }

    // If data exists, show the modal with the payload
    setJsonModal({
      show: true,
      data: payload,
      filename: `${item?.po_number}-${poType === "response_payload" ? "Response" : "Request"}-Payload`,
    });
  }; 
  //M10-13060

  /** ----------------------------------------------
   * 🔹 Render
   * --------------------------------------------- */
  return (
    <InvoiceLayout>
      <header className="flex justify-between items-center py-6 px-[3.75rem] shadow-md">
        <h1 className="text-[#49484C] font-bold text-[2rem]">Purchase Order</h1>
      </header>

      <div className="px-[5.75rem] pt-8">
        <div className="flex justify-between items-center mb-6">
          {/* M10-12019 */}
          {/* M10-12019 */}
          <Button
            onClick={() => navigate("/transactions/purchase-order", {
              state: { isNotRefresh: true }
            })}
            // M10-12019
            className="bg-[#F1F1F1] border border-gray-300 px-4 py-2 text-sm text-[#49484C] hover:bg-gray-100"
          >
            Back to Search
          </Button>

          {poList.length > 1 && (
            <Button
              disabled={loadingPoIds?.size > 0 || !isSubmittablePoBatch || !isApiPushEnabled}
              title={
                !isApiPushEnabled
                  ? "PO API push is not enabled for this company on this module"
                  : !isSubmittablePoBatch
                  ? "PO submit is not allowed for CN-related purchase orders (CNRP, CNSH)"
                  : undefined
              }
              onClick={() => bulkUpload(poList)}
              className="bg-[#F1F1F1] border border-gray-300 px-4 py-2 text-sm text-[#49484C] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingPoIds?.size > 0 && isBulk ? (
                <>
                  <Spinner size="sm" /> Generating...
                </>
              ) : (
                "Generate All API"
              )}
            </Button>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 overflow-auto">
          {isFetchingPo ? (
            <div className="flex justify-center items-center h-[20rem]">
              <Spinner />
            </div>
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead className="bg-gray-100 text-[#616161] sticky top-0">
                <tr>
                  {[
                    "Sr. No.",
                    "Stockist Alias Code",//M10-13484
                    "Stockist Name",
                    "PO Number",
                    "PO Date",
                    "API Status",
                    "API Response",
                    "SO Number",
                    "Generate Date",
                    ...(isRepushApplicable ? ["Enable Re-push"] : []),
                    "Actions",
                  ].map((header, i) => (
                    <th key={i} className="border px-2 py-2 whitespace-nowrap">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {poList.map((item, index) => (
                  <tr key={item?.po_id ?? index} className="border-t hover:bg-gray-50">
                    <td className="border px-2 py-2">{index + 1}</td>
                    <td className="border px-2 py-2">{item?.compStockistAliasCode}</td> {/* M10-13484 */}
                    <td className="border px-2 py-2">{item?.stockist_name}</td>
                    <td className="border px-2 py-2">{item?.po_number}</td>
                    <td className="border px-2 py-2">
                      {moment(item?.created_date).format("DD-MM-YYYY")}
                    </td>
                    <td className="border px-2 py-2">{item?.api_sent_status}</td>
                    <td className="border px-2 py-2">
                      {/* M10-13060 */}
                      {item?.po_api_logs.length > 0 && (
                        <Button
                          onClick={() => handleDownload(item, "response_payload")}
                          className="bg-[#F1F1F1] border border-[#d0d0d0] px-3 py-1 text-xs hover:bg-gray-100"
                        >
                          ⬇️ Download
                        </Button>
                      )}
                      {/* M10-13060 */}
                    </td>
                    <td className="border px-2 py-2">{item?.so_number}</td>
                    <td className="border px-2 py-2">{formattedPoDate}</td>
                    {isRepushApplicable && (
                      <td className="border px-2 py-2 text-center">
                        {/* Enabled only while the push succeeded and no SO number has arrived. */}
                        <input
                          type="checkbox"
                          className="w-4 h-4 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                          checked={isAwaitingSoNumber(item) && Boolean(item?.is_po_repush_enabled)}
                          disabled={!isAwaitingSoNumber(item) || repushUpdatingPoIds.has(item?.po_id)}
                          title={
                            !item?.api_sent_status
                              ? "PO has not been submitted yet"
                              : item?.api_sent_status !== "success"
                              ? "PO submit failed — use Generate API to submit again"
                              : item?.so_number
                              ? "SO number already received"
                              : "Allow this PO to be pushed again"
                          }
                          onChange={(e) => handleRepushToggle(item, e.target.checked)}
                        />
                      </td>
                    )}
                    <td className="border px-2 py-2 flex gap-2">
                      <Button
                        onClick={() =>
                          navigate(`/transactions/purchase-order/${poBatchId}/${item?.po_id}`)
                        }
                        className="bg-[#F1F1F1] border border-[#d0d0d0] px-3 py-1 text-xs hover:bg-gray-100"
                      >
                        View Products
                      </Button>

                      {((!item?.is_api_sent && item?.api_sent_status !== "success") || canRepush(item)) &&
                        !disabledPoIds.has(item?.po_id) && (
                          <Button
                            disabled={loadingPoIds.has(item?.po_id) || !PO_SUBMIT_ALLOWED_TYPES.includes(item?.po_generatetype) || !isApiPushEnabled}
                            title={
                              !isApiPushEnabled
                                ? "PO API push is not enabled for this company on this module"
                                : !PO_SUBMIT_ALLOWED_TYPES.includes(item?.po_generatetype)
                                ? "PO submit is not allowed for CN-related purchase orders (CNRP, CNSH)"
                                : undefined
                            }
                            onClick={() => bulkUpload(item)}
                            className="bg-[#F1F1F1] border border-[#d0d0d0] px-3 py-1 text-xs hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {loadingPoIds?.has(item?.po_id) ? (
                              <>
                                <Spinner size="small" /> Generating...
                              </>
                            ) : (
                              "Generate API"
                            )}
                          </Button>
                        )}
                      {/* M10-13060 */}
                      {item?.po_api_logs.length > 0 && (
                        <Button
                          onClick={() => handleDownload(item, "request_payload")}
                          className="bg-[#F1F1F1] border border-[#d0d0d0] px-3 py-1 text-xs hover:bg-gray-100"
                        >
                          ⬇️ Payload
                        </Button>
                      )}
                      {/* M10-13060 */}
                      {/* M10-14101 */}
                      {[
                        "81739d3a-70f1-706a-4e87-6cdb56506d69", //Ponnu
                        "3e354c07-8918-4963-89f0-093d582f84da", //Arun
                        "d1c3cdfa-a0e1-70ab-0fdf-93c2899a7efb", //Vishnu
                        "21e33dfa-20c1-707e-fbde-d015a307d71b", //Surya
                        "91839d1a-10b1-70d1-db8a-7f99df97d59a", //Yousuf
                      ].some(id => idTokenDetails?.sub?.includes(id)) &&
                        <Button
                          onClick={() => openRevokeModal(item)}
                          className="bg-[#F1F1F1] border border-[#d0d0d0] px-3 py-1 text-xs hover:bg-gray-100"
                          module={fineGrainedPermissionsKeys.INVOICE.REMOVEPO.module}
                          action={fineGrainedPermissionsKeys.INVOICE.REMOVEPO.action}
                        >
                          Revoke PO
                        </Button>}
                      {/* M10-14101 */}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* M10-12438 Revoke PO Modal */}
      <DeleteModal
        title="Revoke PO"
        label="Reference Number"
        schema={revokeSchema}
        value={revokeModal?.show}
        deleteText="Continue"
        setValue={(v: any) =>
          setRevokeModal((prev) => ({ ...prev, show: Boolean(v) }))
        }
        loading={revokeModal?.loading}
        onFormSubmit={handleRevokePo}
      />
      {/* M10-12438 */}

      {/* JSON Modal */}
      {jsonModal?.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-md max-w-2xl w-full h-[80vh] flex flex-col">
            <h2 className="text-lg font-semibold mb-4">JSON Payload Preview</h2>
            <div className="flex-grow overflow-auto bg-gray-100 text-sm p-4 rounded">
              <pre>{JSON.stringify(JSON.parse(jsonModal?.data), null, 2)}</pre>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button onClick={() => setJsonModal({ show: false })}>Close</Button>
              <Button
                className="bg-violet-500 text-white hover:bg-violet-600"
                onClick={() => downloadJson(jsonModal?.filename!, jsonModal?.data)}
              >
                Download JSON
              </Button>
            </div>
          </div>
        </div>
      )}
    </InvoiceLayout>
  );
};

export default PoBulk;